import type { Auth, Proxy, PlainObject } from './types';
import type { FetchResponse, ProxyRequestObject, ProxyResponseBody } from './types/proxy';
import { ProxyMethod, ProxyType } from './types/constants';

import config from './config';
import Utils from './Utils';

export default class ProxyService {
  public sdkInstance: Proxy.SDKInstance = {
    config,
    session: null,
  };
  public requestsNumber: number = 0;
  private currentUserId: number | undefined;
  private abortControllersMap: Proxy.AbortControllersMap = {};
  public requestMethod: typeof ProxyMethod = ProxyMethod;
  public responseType: typeof ProxyType = ProxyType;

  public setSession(session: Partial<Auth.Session> | null): void {
    this.sdkInstance.session = session;

    if (session && session.user_id) {
      this.setCurrentUserId(session.user_id);
    }
  }

  public getSession(): Partial<Auth.Session> | null {
    return this.sdkInstance.session;
  }

  public setCurrentUserId(userId?: number | null): void {
    this.currentUserId = userId || undefined;
  }

  public getCurrentUserId(): number | undefined {
    return this.currentUserId;
  }

  public logRequest(params: Proxy.Params, requestId: number): void {
    Utils.DLog(`[Request][${requestId}]`, `${params.type || 'GET'} ${params.url}`, params);
  }

  public logResponse(response: ProxyResponseBody, requestId: number): void {
    Utils.DLog(`[Response][${requestId}]`, response);
  }

  public buildRequestAndURL(params: Proxy.Params): [ProxyRequestObject, string] {
    const isGetOrHeadType = !params.type || params.type === 'GET' || params.type === 'HEAD';
    const isPostOrPutType = params.type ? params.type === 'POST' || params.type === 'PUT' : false;
    const token = this.sdkInstance && this.sdkInstance.session && this.sdkInstance.session.token;
    const isInternalRequest = params.url.indexOf('s3.amazonaws.com') === -1;
    const isMultipartFormData = params.contentType === false;
    const authKey = params.authKey;
    const requestObject: ProxyRequestObject = {
      method: params.type || 'GET',
    };

    let requestURL = params.url;

    if (params.data) {
      const requestBody = this.buildRequestBody(params, isMultipartFormData, isPostOrPutType);

      if (isGetOrHeadType) {
        requestURL += `?${requestBody}`;
      } else {
        requestObject.body = requestBody as BodyInit;
      }
    }

    if (!isMultipartFormData) {
      requestObject.headers = {
        'Content-Type': isPostOrPutType
          ? 'application/json;charset=utf-8'
          : 'application/x-www-form-urlencoded; charset=UTF-8',
      };
    }

    if (isInternalRequest) {
      if (!requestObject.headers) {
        requestObject.headers = {};
      }

      requestObject.headers['CB-SDK'] = `JS ${config.version} - Client`;

      if (token) {
        requestObject.headers['CB-Token'] = token;
      } else if (authKey) {
        requestObject.headers['CB-AuthKey'] = authKey;
      }
    }

    if (config.timeout) {
      requestObject.timeout = config.timeout;
    }

    return [requestObject, requestURL];
  }

  public buildRequestBody(
    params: Proxy.Params,
    isMultipartFormData: boolean,
    isPostOrPutType: boolean
  ): FormData | string {
    const data = params.data as PlainObject;
    const useArrayQuery = params.useArrayQuery;

    let dataObject: FormData | string;

    if (isMultipartFormData) {
      const formData = new FormData();
      Object.keys(data).forEach(function (item) {
        const fileEntry = data[item] as { data: Blob; name: string } | undefined;

        if (params.fileToCustomObject && item === 'file' && fileEntry) {
          formData.append(item, fileEntry.data, fileEntry.name);
        } else {
          const value = data[item];
          formData.append(item, value instanceof Uint8Array ? new Blob([new Uint8Array(value)]) : (value as string | Blob));
        }
      });
      dataObject = formData;
    } else if (isPostOrPutType) {
      dataObject = JSON.stringify(data);
    } else {
      dataObject = this.serializeQueryParams(data, '', useArrayQuery, 0);
    }

    return dataObject;
  }

  public serializeQueryParams(
    obj: PlainObject | unknown[],
    prefix: string,
    useArrayQuery: boolean | undefined,
    level = 0
  ): string {
    const parts: string[] = [];
    const objRecord = obj as PlainObject;

    for (const propName in objRecord) {
      let propQueryName = this.encodeURIComponent(propName);

      if (Utils.isArray(obj)) {
        propQueryName = '';
      }

      const key = prefix ? prefix + `[${propQueryName}]` : propQueryName;
      let value = objRecord[propName] as PlainObject | unknown[];
      const isArrayVal = Utils.isArray(value);

      if ((isArrayVal && (useArrayQuery || level === 0)) || Utils.isObject(value)) {
        parts.push(this.serializeQueryParams(value, key, useArrayQuery, ++level));
      } else {
        const strVal = isArrayVal ? value.sort().join(',') : String(value);
        parts.push(`${key}=${this.encodeURIComponent(strVal)}`);
      }
    }

    return parts.sort().join('&');
  }

  public encodeURIComponent(uriComponent: string | number | boolean): string {
    return encodeURIComponent(uriComponent).replace(/[#$&+,/:;=?@\[\]]/g, (c) => `%${c.charCodeAt(0).toString(16)}`);
  }

  public abortRequest(abortId: string | number): void {
    if (this.abortControllersMap[abortId]) {
      const controllers = this.abortControllersMap[abortId].controllers || [];

      controllers.forEach((controller) => {
        controller.abort();
      });
    }
  }

  public processSuccessfulOrFailedRequest(abort_id?: string | number): void {
    if (!abort_id || !this.abortControllersMap[abort_id]) {
      return;
    }

    const controllers = this.abortControllersMap[abort_id].controllers || [];

    if (!this.abortControllersMap[abort_id].doneRequestsCount) {
      this.abortControllersMap[abort_id].doneRequestsCount = 1;
    } else {
      this.abortControllersMap[abort_id].doneRequestsCount += 1;
    }

    const doneRequestsCount = this.abortControllersMap[abort_id].doneRequestsCount;

    if (doneRequestsCount === controllers.length) {
      delete this.abortControllersMap[abort_id];
    }
  }

  public async ajax<T>(params: Proxy.Params): Promise<T> {
    const requestId = ++this.requestsNumber;

    return new Promise((resolve, reject) => {
      this.logRequest(params, requestId);

      const [requestObject, requestURL] = this.buildRequestAndURL(params);
      const abort_id = params.abort_id;

      if (abort_id) {
        let index = 0;

        if (this.abortControllersMap[abort_id]) {
          const controllers = this.abortControllersMap[abort_id].controllers || [];
          this.abortControllersMap[abort_id].controllers.push(new AbortController());
          index = controllers.length - 1;
        } else {
          this.abortControllersMap[abort_id] = {
            controllers: [new AbortController()],
          };
        }
        const signal = this.abortControllersMap[abort_id].controllers[index].signal;
        Object.assign(requestObject, { signal });
      }

      let response: FetchResponse | undefined;

      const body = requestObject.body === null ? undefined : requestObject.body;
      const requestInit = { ...requestObject, body };

      // The Promise returned from fetch() won’t reject on HTTP error
      // status even if the response is an HTTP 404 or 500.
      // Instead, it will resolve normally (with ok status set to false),
      // and it will only reject on network failure or if anything prevented the request from completing.
      fetch(requestURL, requestInit as unknown as Parameters<typeof fetch>[1])
        .then((resp: FetchResponse) => {
          response = resp;
          const dataType = params.dataType || ProxyType.JSON;
          return dataType === ProxyType.TEXT ? resp.text() : resp.json();
        })
        .then((body: ProxyResponseBody) => {
          this.processSuccessfulOrFailedRequest(abort_id);
          if (!response!.ok) {
            this.processAjaxError(
              response!,
              body,
              null,
              reject as (reason?: unknown) => void,
              resolve as (value: ProxyResponseBody) => void,
              params,
              requestId
            );
          } else {
            this.processAjaxResponse(body, resolve as (value: ProxyResponseBody) => void, requestId);
          }
        })
        .catch((error: unknown) => {
          this.processSuccessfulOrFailedRequest(abort_id);
          this.processAjaxError(
            response ?? null,
            ' ',
            error,
            reject as (reason?: unknown) => void,
            resolve as (value: ProxyResponseBody) => void,
            params,
            requestId
          );
        });
    });
  }

  public processAjaxResponse(
    body: ProxyResponseBody,
    resolve: (value: ProxyResponseBody) => void,
    requestId: number
  ): void {
    const responseBody = body !== undefined && body !== null && body !== ' ' ? body : 'empty body';
    this.logResponse(responseBody, requestId);

    resolve(body);
  }

  public processAjaxError(
    response: FetchResponse | null,
    body: ProxyResponseBody,
    error: unknown,
    reject: (reason?: unknown) => void,
    resolve: (value: ProxyResponseBody) => void,
    params: Proxy.Params,
    requestId: number
  ): void {
    const err = error as { code?: number; errno?: unknown };
    if (!response && error && !err.code) {
      reject(error);
      return;
    }

    const bodyObj = body as { errors?: unknown } | null;
    const responseBody = body || error || bodyObj?.errors;
    const statusCode = response?.status;
    const code: number | undefined = (response && statusCode) || (error ? err.code : undefined);
    const rawInfo =
      (body && typeof body === 'string' && body !== ' ' ? JSON.parse(body) : body) || (error ? err.errno : undefined);
    const errorObject: Proxy.ErrorObject = {
      code,
      info: rawInfo as Proxy.ErrorObject['info'],
    };

    this.logResponse(responseBody, requestId);

    if ((response?.url ?? '').indexOf(config.urls.session) === -1) {
      if (this.isExpiredSessionError(errorObject) && typeof config.on.sessionExpired === 'function') {
        this.handleExpiredSessionResponse(errorObject, null, reject, resolve, params);
      } else {
        reject(errorObject);
      }
    } else {
      reject(errorObject);
    }
  }

  public handleExpiredSessionResponse(
    error: Proxy.ErrorObject | null,
    response: ProxyResponseBody | null,
    reject: (reason?: unknown) => void,
    resolve: (value: ProxyResponseBody) => void,
    params: Proxy.Params
  ): void {
    const handleResponse = () => {
      if (error) {
        reject(error);
      } else {
        resolve(response);
      }
    };

    const retryCallback = (session: Partial<Auth.Session>) => {
      if (session) {
        this.setSession(session);
        this.ajax(params).then(resolve).catch(reject);
      }
    };

    if (typeof config.on.sessionExpired === 'function') {
      config.on.sessionExpired(handleResponse, retryCallback);
    }
  }

  private isExpiredSessionError(error: Proxy.ErrorObject): boolean {
    try {
      return error && error.code === 401 && error.info?.errors?.base?.[0] === 'Required session does not exist';
    } catch {
      return false;
    }
  }
}
